/**
 * @fileoverview Resin is a user action tracking tool designed to make instrumentation
 * incredibly easy through declarative hierarchical DOM tagging.
 *
 * @author Tarrence van As
 */
window.Resin = window.Resin || {};
/**
 * Attributes for recording a Resin action
 *
 * @typedef {Object} Resin.ActionAttributes
 * @property {string} feature Feature the action belongs to
 * @property {string} component Component the action belongs to
 * @property {string} client Client the action belongs to
 */

/**
 * Resin is a user action tracking tool designed to make instrumentation incredibly easy
 * through declarative hierarchical DOM tagging.
 *
 * @class
 */

Resin = function () {
  'use strict';

  var TARGET_BOUNDARY_ELEMENTS = ['A', 'BUTTON', 'OPTION', 'INPUT', 'SELECT', 'TEXTAREA'],
      TARGET_BOUNDARY_ROLES = ['button', 'link', 'menuitem', 'option', 'listitem'],
      ACTION_WHITE_LIST = 'keypress|click|change|drop|programmatic|doubleclick|rightclick|hover',
      MAX_TARGET_BOUNDARY_DISTANCE = 4,
      ENTER_KEY = 13,
      SPACE_KEY = 32,
      // Regex to ensure a string only consists of alphanumeric characters
  ALPHANUM_REGEX = /^[a-zA-Z0-9|]+$/i,
      RESIN_APPLICATION_ATTRIBUTE = 'data-resin-application',
      RESIN_CLIENT_ATTRIBUTE = 'data-resin-client',
      RESIN_PAGE_ATTRIBUTE = 'data-resin-page',
      RESIN_TARGET_ATTRIBUTE = 'data-resin-target',
      RESIN_ACTION_ATTRIBUTES = [RESIN_TARGET_ATTRIBUTE, 'data-resin-component', RESIN_PAGE_ATTRIBUTE, RESIN_APPLICATION_ATTRIBUTE, RESIN_CLIENT_ATTRIBUTE],
      DEFAULT_EVENTS = {
    click: 'click',
    keypress: 'keypress',
    change: 'change'
  };
  /* User State */

  var active = false,
      previousAction,
      previousWaypoint,
      startTime,
      sessionId,
      handler,
      subscribedEvents = {};
  /**
   * Fires an event with the given name and data.
   * @param {string} type The type of event to fire.
   * @param {Object} data An object with properties that should end up on
   *	  the event object for the given event.
   * @returns {void}
   */

  function fire(type, data) {
    handler.call(this, {
      type: type,
      data: data
    });
  }
  /**
   * IE11 friendly Object.assign method.
   * Automatically assigns to new object, will not modify original objects
   * @param {...Object} argObjs Open-ended Object params
   * @returns {Object}
   */


  function extend(argObjs) {
    var objects = Array.prototype.slice.call(arguments);
    return objects.reduce(function (r, o) {
      Object.keys(o).forEach(function (k) {
        r[k] = o[k];
      });
      return r;
    }, {});
  }
  /**
   * Generates a unique identifier
   *
   * @returns {string} Unique ID
   */


  function generateId() {
    var time = Date.now();
    return 'xxxxxxxxxxxxxxxxxxxxxx'.replace(/x/g, function () {
      // Generate random number between 1 and 16.
      // Using time for added entrophy. User would need to
      // generate a random number with the same seed at the exact
      // same time for a collision to occur.
      var rand = Math.floor((time + Math.random() * 16) % 16); // Convert number to a HEX

      return rand.toString(16);
    });
  }
  /**
   * Finds the nearest action element
   *
   * @param  {HTMLElement} element to search from
   *
   * @returns {HTMLElement|null} The action target element if found else null
   */


  function getActionTargetElement(element) {
    var selectorDistance = 0,
        i,
        targetBoundaryElementsArrLen = TARGET_BOUNDARY_ELEMENTS.length,
        targetBoundaryRolesArrLen = TARGET_BOUNDARY_ROLES.length;

    while (element && !element.disabled) {
      if (MAX_TARGET_BOUNDARY_DISTANCE < selectorDistance) {
        break;
      }

      for (i = 0; i < targetBoundaryElementsArrLen; i++) {
        if (element.nodeName === TARGET_BOUNDARY_ELEMENTS[i]) {
          return element;
        }
      }

      for (i = 0; i < targetBoundaryRolesArrLen; i++) {
        if (element.getAttribute('role') === TARGET_BOUNDARY_ROLES[i]) {
          return element;
        }
      }

      element = element.parentElement;
      selectorDistance++;
    }

    return null;
  }
  /**
   * Create an action log item
   *
   * @param {Resin.ActionAttributes} actionAttributes
   * @returns {Object} Event log object
   */


  function createActionLog(actionAttributes, isWaypoint) {
    var elapsedTime = Date.now() - startTime;
    var previousLog = isWaypoint ? previousWaypoint : previousAction;
    return extend({
      path: window.location.pathname,
      session: sessionId,
      id: generateId(),
      isWaypoint: isWaypoint,
      elapsedTime: elapsedTime,
      prevId: previousLog ? previousLog.id : null,
      prevAction: previousLog ? previousLog.action : null,
      prevTarget: previousLog ? previousLog.target : null,
      prevComponent: previousLog ? previousLog.component : null,
      prevFeature: previousLog ? previousLog.feature : null
    }, actionAttributes);
  }
  /**
   * Determine if an action is a duplicate
   *
   * @param  {Object}  action event action
   *
   * @returns {Boolean} True if duplicate
   */


  function isDuplicateAction(action) {
    return previousAction && action.component === previousAction.component && action.target === previousAction.target && action.elapsedTime < 500;
  }
  /**
   * Determine if a keypress is for a valid action
   *
   * @param {string} actionType
   * @param {Event} event Optional. DOM event object
   * @returns {Boolean} True if valid
   */


  function isValidAction(actionType, event) {
    var targetEl = event.target; // We only log enter andspace key presses since they are the only
    // way to instansiate an action (other than hotkeys)

    if (actionType === 'keypress' && (event.which === ENTER_KEY || event.which === SPACE_KEY)) {
      // Disable logs for 'space' and 'enter' presses on a textarea
      if (targetEl.nodeName === 'TEXTAREA') {
        return false;
      } else if (event.which === SPACE_KEY && targetEl.nodeName === 'INPUT') {
        // Disable logs for 'space' presses in an input field
        return false;
      }

      return true;
    } else if (actionType === 'change' && targetEl.options && typeof targetEl.selectedIndex !== 'undefined') {
      // listen to change events that come from a select field
      return true;
    } else if (actionType === 'change' && typeof targetEl.checked === 'boolean') {
      // Listen to change events tht come from checkbox toggles
      return true;
    } else if (actionType !== 'change' && actionType !== 'keypress') {
      // Ignore 'change' and 'keypress' events since valid ones are filtered above
      return true;
    }

    return false;
  }
  /**
   * Transform an HTML attribute's name to a key suitable for the JSON object.
   * e.g. 'data-resin-some-field' -> 'someField'
   * Attribute's name should begin with 'data-resin' prefix.
   *
   * @param {string} HTML attribute name
   * @returns {string} cleaned attribute name
   */


  function getCleanAttributeKey(attributeName) {
    return attributeName.split('-').slice(2).join('-').replace(/-([a-z0-9])/g, function (match) {
      return match[1].toUpperCase();
    });
  }
  /**
   * Determine whether or not a given HTML attribute's name has 'data-resin' prefix.
   *
   * @param {Object} HTML attribute object
   * @returns {Boolean}
   */


  function isResinAttribute(attribute) {
    return attribute.name.indexOf('data-resin-') === 0;
  }
  /**
   * Sorts the given Resin attributes by specifity.
   * The more specific attributes being first, the less specific being last.
   * For instance: [component, page, target] => [target, component, page]
   *
   * @param {Array} Unsorted list of Resin attributes
   * @returns {Array} Sorted list of Resin attributes
   */


  function sortAttributesBySpecificity(attributes) {
    attributes.sort(function sortBySpecificity(attribute1, attribute2) {
      var attribute1Index = RESIN_ACTION_ATTRIBUTES.indexOf(attribute1.name);
      var attribute2Index = RESIN_ACTION_ATTRIBUTES.indexOf(attribute2.name);
      return attribute1Index - attribute2Index;
    });
    return attributes;
  }
  /**
   * Gets the list of Resin attributes of the given element.
   * The list is sorted by specificity (more specific => less specific).
   * Note: browsers don't sort the attributes list consistently, therefore
   *       it needs to be done programmatically.
   *
   * @param {HTMLElement} element to get the Resin attributes from
   * @returns {Array} Sorted list of Resin attributes for the given element
   */


  function getResinAttributesSortedBySpecificity(element) {
    var attributes = element.attributes,
        attributesCount = attributes.length,
        resinAttributes = [];

    for (var i = 0; i < attributesCount; i++) {
      var attribute = attributes[i];

      if (isResinAttribute(attribute)) {
        resinAttributes.push(attribute);
      }
    }

    return sortAttributesBySpecificity(resinAttributes);
  }
  /**
   * Gets action attributes based on given element
   *
   * @param {HTMLElement} actionTargetEl
   * @returns {Resin.ActionAttributes}
   */


  function getActionAttributes(actionTargetEl) {
    var currentEl = actionTargetEl,
        attributes,
        actionAttributes = {
      target: null,
      feature: null,
      component: null,
      page: null,
      application: null,
      client: null,
      isWaypoint: false,
      data: {}
    },
        foundAttributeIndex = -1; // to keep track of order of resin attributes

    while (currentEl && actionAttributes.client === null) {
      attributes = getResinAttributesSortedBySpecificity(currentEl);

      for (var i = 0; i < attributes.length; i++) {
        var attribute = attributes[i];
        var attributeKey = getCleanAttributeKey(attribute.name);
        var indexInAttributeArray = RESIN_ACTION_ATTRIBUTES.indexOf(attribute.name);

        if (indexInAttributeArray - 1 === foundAttributeIndex) {
          foundAttributeIndex++;
          actionAttributes[attributeKey] = attribute.value;
        } else if (indexInAttributeArray >= 0) {// attribute found out of order; no-op
        } else if (attribute.name === 'data-resin-waypoint') {
          actionAttributes.isWaypoint = attribute.value === 'true';
        } else if (attribute.name === 'data-resin-feature') {
          // feature can be out of order
          if (!actionAttributes.feature) {
            // We should not override a feature attribute defined on a descendent
            actionAttributes.feature = attribute.value;
          }
        } else if (!actionAttributes.data[attributeKey]) {
          // This is a data attribute
          actionAttributes.data[attributeKey] = attribute.value;
        }
      }

      currentEl = currentEl.parentElement;
    }

    return actionAttributes;
  }
  /**
   * Checks if given action attributes are valid.
   *
   * @param {Resin.ActionAttributes} actionAttributes
   * @returns {Boolean} True if valid, false otherwise.
   */


  function isValidActionAttributes(actionAttributes) {
    if (!actionAttributes.target) {
      return false;
    } else if (actionAttributes.feature && !actionAttributes.feature.match(ALPHANUM_REGEX)) {
      console.warn('Resin: Feature must be an alphanum');
      return false;
    } else if (!actionAttributes.component) {
      console.warn('Resin: Component not found');
      return false;
    } else if (!actionAttributes.component.match(ALPHANUM_REGEX)) {
      console.warn('Resin: Component must be an alphanum');
      return false;
    } else if (!actionAttributes.page) {
      console.warn('Resin: Page not found');
      return false;
    } else if (!actionAttributes.page.match(ALPHANUM_REGEX)) {
      console.warn('Resin: Page must be an alphanum');
      return false;
    } else if (!actionAttributes.application) {
      console.warn('Resin: Application not found');
      return false;
    } else if (!actionAttributes.application.match(ALPHANUM_REGEX)) {
      console.warn('Resin: Application must be an alphanum');
      return false;
    } else if (!actionAttributes.client) {
      console.warn('Resin: Client not found');
      return false;
    } else if (!actionAttributes.client.match(ALPHANUM_REGEX)) {
      console.warn('Resin: Client must be an alphanum');
      return false;
    }

    return true;
  }
  /**
   * mouseup event handler
   *
   * @param  {string} actionType An action type
   *
   * @returns {void}
   */


  function onActionHandler(actionType) {
    return function (event) {
      var targetEl = event.target,
          actionTargetEl,
          action,
          actionAttributes,
          isWaypoint,
          waypoint,
          waypointAttributes;

      if (!isValidAction(actionType, event)) {
        return;
      }

      if (actionType === 'change' && targetEl.options) {
        actionTargetEl = targetEl.options[targetEl.selectedIndex];
      } else {
        actionTargetEl = getActionTargetElement(targetEl);
      }

      if (actionTargetEl) {
        actionAttributes = getActionAttributes(actionTargetEl);

        if (!actionAttributes || !isValidActionAttributes(actionAttributes)) {
          return;
        }

        actionAttributes.action = actionType;
        isWaypoint = actionAttributes.isWaypoint;
        waypointAttributes = extend(actionAttributes);
        actionAttributes.isWaypoint = false;
        action = createActionLog(actionAttributes, false); // Avoid creating duplicate logs if two events are
        // fired for an action, i.e. focus and click
        // or if the events happened unreasonably fast
        // i.e. programmatic focus onload

        if (isDuplicateAction(action) || action.elapsedTime < 200) {
          return;
        } // If waypoint, create waypoint log item


        if (isWaypoint) {
          waypoint = createActionLog(waypointAttributes, true);
          previousWaypoint = waypoint;
          fire('useraction', waypoint);
        }

        fire('useraction', action);
        previousAction = action;
        startTime = Date.now();
      }
    };
  }
  /**
   * Test to see browser can use session storage.
   * See http://stackoverflow.com/questions/14555347
   * Note that this will return false if we are actually hitting the maximum sessionStorage
   * size (5MB / 2.5M chars)
   *
   * @returns {boolean} True if browser can use sessionStore
   */


  function canUseSessionStorage() {
    try {
      sessionStorage.setItem('testKey', 'testValue');
      sessionStorage.removeItem('testKey');
      return true;
    } catch (e) {
      return false;
    }
  }
  /**
   * Initialize a session for the user
   *
   * @returns {void}
   */


  function initSesssion() {
    if (!canUseSessionStorage()) {
      return;
    }

    var lastActionTime = sessionStorage.getItem('resin-last-action-time');
    sessionId = sessionStorage.getItem('resin-session-id'); // Check if session store id and last action time are valid
    // and last action time is within the last 30minutes

    if (!sessionId || !lastActionTime || Date.now() - lastActionTime > 1800000) {
      sessionId = generateId();
      sessionStorage.setItem('resin-session-id', sessionId);
      sessionStorage.setItem('resin-last-action-time', startTime);
    }
  }

  function subscribeToEvents(events) {
    var _handler;

    for (var property in events) {
      if (events.hasOwnProperty(property)) {
        _handler = onActionHandler(property);
        document.addEventListener(events[property], _handler);
        subscribedEvents[property] = _handler;
      }
    }
  }

  function unsubscribeFromEvents() {
    for (var eventType in subscribedEvents) {
      if (subscribedEvents.hasOwnProperty(eventType)) {
        document.removeEventListener(eventType, subscribedEvents[eventType]);
      }
    }
  }
  /**
   * Populates missing resin attributes using global selectors.
   * An application should never define these attributes more than once, but in the case that they do, we just use the first instance.
   * This may cause unexpected results so we suggest users pass in these values explicitly to override this behavior.
   *
   * @param {Resin.ActionAttributes} actionAttributes
   * @returns {Resin.ActionAttributes}
   */


  function populateGlobalResinAttributes(actionAttributes) {
    // Make a copy of the attributes object
    var attributes = extend(actionAttributes);

    if (!attributes.client) {
      var resinClientEl = document.querySelector('[' + RESIN_CLIENT_ATTRIBUTE + ']');
      attributes.client = resinClientEl ? resinClientEl.getAttribute(RESIN_CLIENT_ATTRIBUTE) : null;
    }

    if (!attributes.application) {
      var resinApplicationEl = document.querySelector('[' + RESIN_APPLICATION_ATTRIBUTE + ']');
      attributes.application = resinApplicationEl ? resinApplicationEl.getAttribute(RESIN_APPLICATION_ATTRIBUTE) : null;
    }

    if (!attributes.page) {
      var resinPageEl = document.querySelector('[' + RESIN_PAGE_ATTRIBUTE + ']');
      attributes.page = resinPageEl ? resinPageEl.getAttribute(RESIN_PAGE_ATTRIBUTE) : null;
    }

    return attributes;
  }

  return {
    /**
     * Starts listening and recording DOM activities.
     *
     * @param {Function} cb Function to call when event occurs.
     * @param {Object} params Events and action types to listen to.
     * @returns {void}
     */
    start: function start(cb, params) {
      var events = params && params.events ? extend(DEFAULT_EVENTS, params.events) : DEFAULT_EVENTS;

      if (!active) {
        startTime = Date.now();
        initSesssion();
        subscribeToEvents(events);
        handler = cb;
        active = true;
      } else {
        throw new Error('Resin is already started');
      }
    },

    /**
     * Stops listening to DOM activities.
     *
     * @returns {void}
     */
    stop: function stop() {
      previousAction = null;
      startTime = null;
      sessionId = null;
      handler = null;
      active = false;
      unsubscribeFromEvents();
    },

    /**
     * Get the session id that is currently being used by resin to log
     * actions. Used in requests where we need to tie backend logs to resin data.
     * @returns {string} Session ID
     */
    getSessionID: function getSessionID() {
      return sessionStorage.getItem('resin-session-id');
    },

    /**
     * Record a users action programmatically.
     *
     * @param {Resin.ActionAttributes} actionAttributes
     * @returns {void}
     */
    recordAction: function recordAction(actionAttributes) {
      // When resin is not active, no-op
      if (!active) {
        return;
      } // Ignore actions without targets


      if (!actionAttributes.target) {
        return;
      } // Action must be in the white list to be recorded


      var isWhiteListedAction = ACTION_WHITE_LIST.indexOf(actionAttributes.action) !== -1;

      if (!isWhiteListedAction) {
        console.warn('Resin: Action ' + actionAttributes.action + ' is not whitelisted');
        return;
      } // Populate resin values such as client, application, and page


      var attributes = populateGlobalResinAttributes(actionAttributes);
      var actionLog = createActionLog(attributes);
      fire('useraction', actionLog);
      previousAction = actionLog;
      startTime = Date.now();
    },

    /**
     * Record a users action programmatically as if given element is the resin target
     *
     * @param {HTMLElement} element
     * @param {?Object} actionAttributeOverrides
     * @returns {void}
     */
    recordActionFromElement: function recordActionFromElement(element, actionAttributeOverrides) {
      var overrides = actionAttributeOverrides || {};
      var attributes = this.getActionAttributesFromElement(element); // Need to merge nested resin free-form data

      if (attributes.data && overrides.data) {
        overrides.data = extend(attributes.data, overrides.data);
      }

      this.recordAction(extend(attributes, overrides));
    },

    /**
     * Gets action attributes based on given element
     *
     * @param {HTMLElement} actionTargetEl
     * @returns {Resin.ActionAttributes}
     */
    getActionAttributes: getActionAttributes,

    /**
     * Gets action attributes as if given element is the resin target
     *
     * @param {HTMLElement} element
     * @returns {Resin.ActionAttributes}
     */
    getActionAttributesFromElement: function getActionAttributesFromElement(element) {
      var hasResinTargetAttribute = element.hasAttribute(RESIN_TARGET_ATTRIBUTE); // If the given element does not have a "data-resin-target" attribute,
      // add one so that Resin can retrieve all other attributes.

      if (!hasResinTargetAttribute) {
        element.setAttribute(RESIN_TARGET_ATTRIBUTE, '');
      }

      var attributes = this.getActionAttributes(element);

      if (!hasResinTargetAttribute) {
        element.removeAttribute(RESIN_TARGET_ATTRIBUTE);
      }

      return attributes;
    }
  };
}();